import {NewInvestGameCta} from './SettingsService.types';
import {DI_TOKENS} from '@/di/di.tokens';
import {SettingsType} from '@/stores/SettingsStore/SettingsStore.constants';

import {injected} from 'brandi';

import {AbstractStorage} from '@specials/localstorage';
import {HttpError} from '@specials/project-user';

import {LSKeys} from '@/constants';
import {ErrorsService} from '@/services/ErrorsService/ErrorsService';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {PlatformService} from '@/services/PlatformService/PlatformService';
import {IProjectApi} from '@/services/ProjectApi/types';
import {GameStatusService} from '@/services/shared/GameStatusService/GameStatusService';
import {SettingsStore} from '@/stores/SettingsStore/SettingsStore';
import {ClickerAudioPlayer} from '@/ui/pages/ClickerGamePage/clicker-game/ClickerAudioPlayer';

export class SettingsService {
  constructor(
    private readonly projectApi: IProjectApi,
    private readonly settingsStore: SettingsStore,
    private readonly loggerService: LoggerService,
    private readonly localStorageService: AbstractStorage,
    private readonly clickerGameAudioPlayer: ClickerAudioPlayer,
    private readonly errorsService: ErrorsService,
    private readonly gameStatusService: GameStatusService,
    private readonly platformService: PlatformService,
  ) {}

  public async toggleSetting(type: SettingsType): Promise<void> {
    try {
      switch (type) {
        case SettingsType.Reminders:
          await this.toggleSettingReminder();
          break;
        case SettingsType.Music:
          await this.toggleSettingMusic();
          break;
        case SettingsType.Sounds:
          await this.toggleSettingSounds();
          break;
        case SettingsType.PerformanceMode:
          this.toggleSettingPerformanceMode();
          break;
      }
    } catch (error) {
      this.loggerService.logHttpError(error as HttpError);

      if (this.errorsService.isErrorRecalculationLeagues(error)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
      } else {
        throw error;
      }
    }
  }

  public getLinkToNewInvestGame = (cta: NewInvestGameCta, isMobile: boolean): string | null => {
    if (this.platformService.isInvesting()) {
      return cta.investAppDeeplink;
    }

    if (this.platformService.isMb()) {
      return cta.mbAppDeeplink;
    }

    if (this.platformService.detectIsIos()) {
      return cta.mobileLinkIos;
    }

    if (isMobile) {
      return cta.mobileLink;
    }

    return null;
  };

  private toggleSettingReminder = async (): Promise<void> => {
    const remindersEnabled = this.settingsStore.getSettingValue(SettingsType.Reminders);

    await this.projectApi.updateSettings({
      remindersEnabled: !remindersEnabled,
    });

    this.settingsStore.setRemindersEnabled(!remindersEnabled);
  };

  private toggleSettingMusic = async (): Promise<void> => {
    const musicEnabled = this.settingsStore.getSettingValue(SettingsType.Music);

    await this.projectApi.updateSettings({
      musicEnabled: !musicEnabled,
    });

    this.settingsStore.setMusicEnabled(!musicEnabled);
    this.clickerGameAudioPlayer.checkMusicMute();
  };

  private toggleSettingSounds = async (): Promise<void> => {
    const soundsEnabled = this.settingsStore.getSettingValue(SettingsType.Sounds);

    await this.projectApi.updateSettings({
      soundsEnabled: !soundsEnabled,
    });

    this.settingsStore.setSoundsEnabled(!soundsEnabled);
    this.clickerGameAudioPlayer.checkSoundsMute();
  };

  private toggleSettingPerformanceMode = (): void => {
    const performanceModeEnabled = this.settingsStore.getSettingValue(SettingsType.PerformanceMode);

    this.localStorageService.setItem(LSKeys.PerformanceModeEnabled, `${!performanceModeEnabled}`);
    this.settingsStore.setPerformanceModeEnabled(!performanceModeEnabled);
  };
}

injected(
  SettingsService,
  DI_TOKENS.projectApi,
  DI_TOKENS.settingsStore,
  DI_TOKENS.loggerService,
  DI_TOKENS.localStorageService,
  DI_TOKENS.clickerGameAudioPlayer,
  DI_TOKENS.errorsService,
  DI_TOKENS.gameStatusService,
  DI_TOKENS.platformService,
);
